import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useParams, useNavigate, Link} from 'react-router-dom'
import {
    Activity,
    Building2,
    CirclePlay,
    CirclePause,
    CopyPlus,
    Cpu,
    Cog,
    MonitorCheck,
    MonitorDot,
    ReceiptText,
    SquareArrowOutUpRight,
    Wallet
} from 'lucide-react';
import {DownloadOutlined, UploadOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {Empty, Spin, Tag} from "antd";
import {APIv1} from '../../../api'
import {images} from "../../../constants";
import {
    deviceStatusInactiveTimeToText,
    extractDateBySecond
} from "../../../utils";
import {deviceStatusInactiveIcons} from "../../../utils/deviceUtils";
import DeviceFMHistoryComponent
    from "../../../components/DeviceStatus/DeviceFMHistoryComponent/DeviceFMHistoryComponent";
import DevicePaymentProvidersView from "./DevicePaymentProvidersView";
import DeviceOperations from "./DeviceOperations";
import CopyDeviceDataModal from "../../../models/devices/device.detail.model";
import DeviceLogsSection from "../../../components/Device/Detail/DeviceLogsSection/DeviceLogsSection";
import YandexLocationMap from "../../../components/Maps/YandexLocationMap/YandexLocationMap";
import Orders from "../../Orders/Orders";
import ExpandableSection from "../../../components/Detail/ExpandableSection/ExpandableSection";
import './DeviceDetail.scss'

const DEVICE_COPY_SECTIONS = ['main', 'market', 'paymentProviders', 'config'];

const DeviceDetail = () => {
    const {serial_number} = useParams();
    const {t} = useTranslation();
    const [userData, setUserData] = useState({});
    const [deviceData, setDeviceData] = useState(null);
    const [relatedDevices, setRelatedDevices] = useState([]);
    const [relatedSearch, setRelatedSearch] = useState('');
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [copyModalOpen, setCopyModalOpen] = useState(false);
    const [selectedCopySections, setSelectedCopySections] = useState(DEVICE_COPY_SECTIONS);
    const [mapExpanded, setMapExpanded] = useState(false);
    const mapSectionRef = useRef(null);

    const extractDate = useCallback((dateString) => {
        return dateString ? new Date(dateString).toISOString().slice(0, 10) : '----/--/--';
    }, []);

    const fetchData = useCallback(async (serialNumber, token) => {
        setLoading(true);
        try {
            const deviceResponse = await APIv1.get(`/device/status/retrieve/${serialNumber}`, {
                headers: {
                    Authorization: `Token ${token}`,
                },
            });
            const deviceSerialNumber = deviceResponse.data.device_serial_number;
            const requestConfig = {
                headers: {
                    Authorization: `Token ${token}`,
                },
            };

            setDeviceData(deviceResponse.data);
            const relatedDevicesResponse = await APIv1.get(`/devices/get_related_devices/?serial=${encodeURIComponent(deviceSerialNumber)}`, requestConfig);

            setRelatedDevices(relatedDevicesResponse.data);
        } catch (err) {
            console.error('Something went wrong:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const items = JSON.parse(localStorage.getItem('user'))
        if (items) {
            setUserData(items)
        }
    }, []);

    useEffect(() => {
        if (userData.token && serial_number) {
            fetchData(serial_number, userData.token)
        }
    }, [userData.token, serial_number, fetchData]);

    useEffect(() => {
        if (mapExpanded) {
            requestAnimationFrame(() => {
                mapSectionRef.current?.scrollIntoView({behavior: "smooth", block: "start"});
            });
        }
    }, [mapExpanded]);

    const renderRelatedDevices = () => {
        const filtered = relatedDevices.filter(item =>
            item.device_serial_number
                .toString()
                .toLowerCase()
                .includes(relatedSearch.toLowerCase())
        );

        if (filtered.length === 0) {
            return <li>No related devices found.</li>;
        }

        return (
            <div className="detail-view__related-devices">
                {filtered.map(item => (
                    <div
                        key={item.device_serial_number}
                        className={`detail-view__related-device ${Number(serial_number) === Number(item.id) ? 'detail-view__related-device--active' : ''}`}
                        onClick={() => onRelatedDevices(item)}
                    >
                        <span>{item.device_serial_number}</span>
                    </div>
                ))}
            </div>
        );
    };

    const onScrollToTop = () => {
        window.scrollTo(0, 0)
    }

    const onRelatedDevices = (item) => {
        navigate(`/device/detail/${item.device_serial_number}`)
        onScrollToTop();
    }

    const handleCreateDeviceNavigate = () => {
        navigate(`/create/device/${deviceData.device_serial_number}`);
    };

    const getCopySectionOptions = () => [
        {
            label: t("pages.devices.detailColumns.copyDevice.sections.main"),
            description: t("pages.devices.detailColumns.copyDevice.sectionDescriptions.main"),
            value: "main",
            icon: <MonitorCheck size={18}/>,
        },
        {
            label: t("pages.devices.detailColumns.copyDevice.sections.market"),
            description: t("pages.devices.detailColumns.copyDevice.sectionDescriptions.market"),
            value: "market",
            icon: <Building2 size={18}/>,
        },
        {
            label: t("pages.devices.detailColumns.copyDevice.sections.paymentProviders"),
            description: t("pages.devices.detailColumns.copyDevice.sectionDescriptions.paymentProviders"),
            value: "paymentProviders",
            icon: <Wallet size={18}/>,
        },
        {
            label: t("pages.devices.detailColumns.copyDevice.sections.config"),
            description: t("pages.devices.detailColumns.copyDevice.sectionDescriptions.config"),
            value: "config",
            icon: <Cog size={18}/>,
        },
    ];

    const openCopyModal = () => {
        setSelectedCopySections(DEVICE_COPY_SECTIONS);
        setCopyModalOpen(true);
    };

    const closeCopyModal = () => {
        setCopyModalOpen(false);
    };

    const updateCopySection = (section, checked) => {
        setSelectedCopySections(prev => {
            if (checked) {
                return prev.includes(section) ? prev : [...prev, section];
            }

            return prev.filter(item => item !== section);
        });
    };

    const selectAllCopySections = () => {
        setSelectedCopySections(DEVICE_COPY_SECTIONS);
    };

    const clearCopySections = () => {
        setSelectedCopySections([]);
    };

    const handleCopyDeviceNavigate = () => {
        const searchParams = new URLSearchParams({
            mode: "copy",
            sections: selectedCopySections.join(","),
        });

        navigate(`/create/device/${deviceData.device_serial_number}?${searchParams.toString()}`);
    };

    const getValueByPath = (source, path) => {
        return path.split('.').reduce((value, key) => value?.[key], source);
    };

    const getDeviceValue = (key) => (
        deviceData.status?.[key]
        ?? deviceData.runtime?.[key]
        ?? deviceData.device?.[key]
        ?? deviceData[key]
    );

    const renderEmpty = () => (
        <span className="detail-view__tag detail-view__tag--empty">
            {t("common.detailPages.empty")}
        </span>
    );

    const renderValue = (value) => {
        if (value === true) {
            return (
                <span className="detail-view__tag detail-view__tag--success">
                    {t("common.detailPages.access")}
                </span>
            );
        }

        if (value === false) {
            return (
                <span className="detail-view__tag detail-view__tag--error">
                    {t("common.detailPages.decline")}
                </span>
            );
        }

        return value || value === 0 ? value : renderEmpty();
    };

    const getDeviceShiftOpen = () => (
        deviceData.status?.is_shift_open
        ?? deviceData.runtime?.is_shift_open
        ?? deviceData.is_shift_open
        ?? deviceData.device?.is_shift_open
    );

    const renderShiftOpen = () => {
        const isShiftOpen = getDeviceShiftOpen();

        if (typeof isShiftOpen !== 'boolean') {
            return renderEmpty();
        }

        return (
            <span className={`detail-view__tag detail-view__tag--${isShiftOpen ? 'success' : 'error'}`}>
                {isShiftOpen
                    ? <CirclePlay size={14}/>
                    : <CirclePause size={14}/>}
                {isShiftOpen
                    ? t("pages.devices.detailColumns.container2.shiftOpen")
                    : t("pages.devices.detailColumns.container2.shiftClosed")}
            </span>
        );
    };

    const renderDetailItem = ({label, value, children, labelExtra, valueClassName = ""}) => (
        <li className="detail-view__item" key={label}>
            <span className="detail-view__label">
                {label}:
                {labelExtra}
            </span>
            {!labelExtra && (
                <span className={`detail-view__value ${valueClassName}`}>{children || renderValue(value)}</span>
            )}
        </li>
    );

    const renderSection = ({index, title, icon, items, className = ""}) => (
        <section className={`detail-view__section ${className}`}>
            <div className="detail-view__section-header">
                <span className="detail-view__section-index">{index}</span>
                <div className="detail-view__section-heading">
                    {icon && <span className="detail-view__section-icon">{icon}</span>}
                    <h2 className="detail-view__section-title">{title}</h2>
                </div>
            </div>
            <ul className="detail-view__list">
                {items.map(renderDetailItem)}
            </ul>
        </section>
    );

    return (
        <div className="detail-view">
            {loading ? (
                <>
                    <div className="detail-view__loading">
                        <Spin size="large"/>
                    </div>
                </>
            ) : !deviceData ? (
                <div className="detail-view__loading">
                    <Empty description={t("pages.devices.addNewDeviceColumns.messages.deviceNotFound")}/>
                </div>
            ) : (
                <div className="detail-view__container">
                    <div className="detail-view__header">
                        <div className="detail-view__title">
                            <span className="detail-view__eyebrow">Device profile</span>
                            <h1 className="detail-view__main-title">
                                {deviceData.device_serial_number}
                                <Tag color="#108ee9" className="detail-view__main-version">
                                    {deviceData.device.fbox_version}
                                </Tag>
                            </h1>
                            <span className="detail-view__subtitle">
                                <span className="detail-view__subtitle-label">
                                    {t("pages.devices.detailColumns.subtitle")}:
                                </span>
                                <span>
                                    {deviceData.company.name}
                                </span>
                            </span>
                        </div>
                        <div className="detail-view__header-actions">
                            <div className="detail-view__action-buttons">
                                {userData?.data?.is_admin && (
                                    <DeviceOperations
                                        serialNumber={deviceData.device_serial_number}
                                        token={userData.token}
                                    />
                                )}
                                <button
                                    className="detail-view__action-button detail-view__action-button--secondary"
                                    onClick={() => handleCreateDeviceNavigate()}
                                >
                                    {t("common.edit")}
                                </button>
                                <button
                                    className="detail-view__action-button detail-view__action-button--secondary"
                                    onClick={openCopyModal}
                                >
                                    <CopyPlus size={16}/>
                                    {t("pages.devices.detailColumns.copyDevice.button")}
                                </button>
                                <button
                                    className="detail-view__action-button detail-view__action-button--secondary"
                                    onClick={() => navigate(-1)}
                                >
                                    {t("pages.devices.detailColumns.button2")}
                                </button>
                            </div>
                            <button
                                type="button"
                                className="detail-view__map-toggle"
                                aria-expanded={mapExpanded}
                                onClick={() => setMapExpanded(prev => !prev)}
                            >
                                <img
                                    src={images.fiscalboxMapCard}
                                    alt="Посмотреть на карте"
                                    className="detail-view__map-toggle-image"
                                />
                                <span className="detail-view__map-toggle-label">Посмотреть на карте</span>
                            </button>
                        </div>
                    </div>

                    <CopyDeviceDataModal
                        open={copyModalOpen}
                        copySections={getCopySectionOptions()}
                        selectedSections={selectedCopySections}
                        onCancel={closeCopyModal}
                        onConfirm={handleCopyDeviceNavigate}
                        onSelectAll={selectAllCopySections}
                        onClear={clearCopySections}
                        onSectionChange={updateCopySection}
                        t={t}
                    />

                    <div className="detail-view__content">
                        <div className="detail-view__main-info">
                            <div className="detail-view__grid">
                                {renderSection({
                                    index: "01",
                                    title: t("pages.devices.detailColumns.container1.title"),
                                    icon: <Building2 size={16}/>,
                                    items: [
                                        {
                                            label: t("common.inn"),
                                            children: (
                                                <Link to={`/company/detail/${deviceData.company.id}`}>
                                                    <Tag color="magenta" className="detail-view__main-version">
                                                        {deviceData.company.inn}
                                                        <SquareArrowOutUpRight size={14} style={{marginLeft: 5}}/>
                                                    </Tag>
                                                </Link>
                                            ),
                                        },
                                        {
                                            label: t("pages.devices.detailColumns.container1.row1"),
                                            value: deviceData.user.username
                                        },
                                        {
                                            label: "Address",
                                            value: deviceData.device?.address,
                                        },
                                        {
                                            label: t("pages.devices.detailColumns.container1.row6"),
                                            value: extractDate(deviceData.device.start_date)
                                        },
                                        {
                                            label: t("pages.devices.detailColumns.container1.row7"),
                                            value: extractDate(deviceData.device.end_date)
                                        },
                                        {
                                            label: t("pages.devices.detailColumns.container2.version_number"),
                                            value: deviceData.status?.version_number !== deviceData.device.version?.version_number
                                                ? (
                                                    <span
                                                        style={{display: "inline-flex", alignItems: "center", gap: 10}}>
                                                        {deviceData.status?.version_number} <DownloadOutlined/>
                                                        {deviceData.device.version?.version_number} <UploadOutlined/>
                                                    </span>
                                                )
                                                : deviceData.device.version?.version_number
                                        },
                                        {
                                            label: t("pages.devices.detailColumns.container1.row10"),
                                            value: deviceData.status?.device_ip_address
                                        },
                                        {
                                            label: t("pages.devices.detailColumns.container1.row9"),
                                            value: deviceData.device.last_order_number
                                        },
                                        {
                                            label: t("pages.devices.detailColumns.container1.row11"),
                                            value: getDeviceValue("terminal_id")
                                        },
                                    ],
                                })}

                                {renderSection({
                                    index: "02",
                                    title: t("pages.devices.detailColumns.container2.title"),
                                    icon: <Activity size={16}/>,
                                    items: [
                                        {
                                            label: t("pages.devices.detailColumns.container1.row4"),
                                            children: (
                                                <span
                                                    className={`detail-view__tag detail-view__tag--${deviceData.device.status.toLowerCase()}`}>
                                                    {deviceData.device.status}
                                                </span>
                                            ),
                                        },
                                        {
                                            label: t("pages.devices.detailColumns.container1.row12"),
                                            value: deviceData.device.disconnect_reason,
                                            valueClassName: deviceData.device.disconnect_reason
                                                ? "detail-view__value--long"
                                                : "",
                                        },
                                        {
                                            label: t("pages.devices.detailColumns.container1.row8"),
                                            children: (
                                                <span className="detail-view__activity-value">
                                                    {deviceStatusInactiveTimeToText[deviceData.is_active_time]} ...
                                                    {deviceData.is_active
                                                        ? <MonitorCheck size={18} color={'#1cb344'}/>
                                                        : <MonitorDot size={18}
                                                                      color={deviceStatusInactiveIcons[deviceData.is_active_time]}/>}
                                                </span>
                                            ),
                                        },
                                        {
                                            label: t("pages.devices.detailColumns.container2.is_shift_open"),
                                            children: renderShiftOpen()
                                        },
                                        {
                                            label: t("pages.devices.detailColumns.container2.z_report_left_count"),
                                            value: deviceData.status?.z_report_left_count
                                        },
                                        {
                                            label: t("pages.devices.detailColumns.container2.orders_not_sent_count"),
                                            value: deviceData.status?.orders_not_sent_count
                                        },
                                        {
                                            label: t("pages.devices.detailColumns.container1.row2"),
                                            value: deviceData.device.is_multi_user
                                        },
                                        {
                                            label: t("pages.devices.detailColumns.container1.row3"),
                                            value: deviceData.device.update_available
                                        },
                                        {
                                            label: t("pages.devices.detailColumns.container1.row5"),
                                            value: extractDateBySecond(deviceData.status?.updated_date)
                                        },
                                    ],
                                })}

                                {renderSection({
                                    index: "03",
                                    title: t("pages.devices.detailColumns.container3.title"),
                                    icon: <Cpu size={16}/>,
                                    items: [
                                        {
                                            label: t("pages.devices.detailColumns.container3.mac_address"),
                                            value: deviceData.device.config.mac_address
                                        },
                                        {
                                            label: t("pages.devices.detailColumns.container3.printer_name"),
                                            value: deviceData.device.config.printer_name
                                        },
                                        {
                                            label: t("pages.devices.detailColumns.container3.printer_model"),
                                            value: deviceData.device.config.printer_model
                                        },
                                        {
                                            label: t("pages.devices.detailColumns.container3.printer_number"),
                                            value: deviceData.device.config.printer_number
                                        },
                                        {
                                            label: t("pages.devices.detailColumns.container3.printer_type"),
                                            value: deviceData.device.config.printer_type
                                        },
                                        {
                                            label: t("pages.devices.detailColumns.container3.pos_terminal.type"),
                                            value: getValueByPath(deviceData.device, "pos_terminal.type")
                                        },
                                        {
                                            label: t("pages.devices.detailColumns.container3.pos_terminal.terminal_serial_number"),
                                            value: getValueByPath(deviceData.device, "pos_terminal.terminal_serial_number")
                                        },
                                        {
                                            label: t("pages.devices.detailColumns.container3.pos_terminal.tms_id"),
                                            value: getValueByPath(deviceData.device, "pos_terminal.tms_id")
                                        },
                                    ],
                                })}
                            </div>

                            <section className="detail-view__section">
                                <div className="detail-view__section-header">
                                    <span className="detail-view__section-index">04</span>
                                    <div className="detail-view__section-heading">
                                        <span className="detail-view__section-icon"><Wallet size={16}/></span>
                                        <h2 className="detail-view__section-title">
                                            {t("pages.devices.addNewDeviceColumns.paymentSectionTitle")}
                                        </h2>
                                    </div>
                                </div>
                                <DevicePaymentProvidersView device={deviceData.device} t={t}/>
                            </section>

                            <section className="detail-view__section">
                                <div className="detail-view__section-header">
                                    <span className="detail-view__section-index">05</span>
                                    <div className="detail-view__section-heading">
                                        <span className="detail-view__section-icon"><Cog size={16}/></span>
                                        <h2 className="detail-view__section-title">{t("pages.devices.addNewDeviceColumns.configSectionTitle")}</h2>
                                    </div>
                                </div>
                                <ul className="detail-view__config-grid">
                                    {[
                                        ["Market name", "market_name"],
                                        ["Latitude", "lat"],
                                        ["Longitude", "long"],
                                        ["Cash desc number", "cash_desc_number"],
                                        ["MAC address", "mac_address"],
                                        ["Printer type", "printer_type"],
                                        ["Printer model", "printer_model"],
                                        ["Printer name", "printer_name"],
                                        ["Printer number", "printer_number"],
                                        ["IP address", "ip_address"],
                                        ["DNS", "dns"],
                                        ["Gateway", "gateway"],
                                        ["Case", "case"],
                                        ["Line char count", "line_char_count"],
                                        ["Additional text", "additional_text"],
                                        ["Xumo address", "xumo_address"],
                                        ["Factory ID", "factory_id"],
                                        ["Payment connector IP", "payment_connector_ip"],
                                        ["Space after print", "space_after_print"],
                                        ["MXIK timeout", "mxik_timeout"],
                                        ["Left Z reports validate", "left_z_reports_validate"],
                                        ["Special version", "special_version"],
                                        ["Reader", "reader"],
                                        ["Local labels disabled", "local_labels_disabled"],
                                        ["Save orders", "save_orders"],
                                        ["Check time", "check_time"],
                                        ["Local MXIK disable", "local_mxik_disable"],
                                        ["Auto cut banner", "auto_cut_banner"],
                                        ["New API enabled", "new_api_enabled"],
                                        ["Over cash not permitted", "over_cash_not_permitted"],
                                        ["OKM validation", "okm_validation"],
                                        ["Mark version", "mark_version"],
                                        ["QR icon center", "qr_icon_center"],
                                        ["Location updated date", "location_updated_date"],
                                        ["OFD registration date", "ofd_registration_date"],
                                        ["Server token", "server_token"],
                                        ["Save order", "save_order"],
                                        ["Ethernet IP", "eth_ip"],
                                        ["Wi-Fi IP", "wi_fi_ip"],
                                    ].map(([label, field]) => renderDetailItem({
                                        label,
                                        value: field.includes("date") ? extractDate(deviceData.device[field]) : deviceData.device.config[field],
                                    }))}
                                </ul>
                            </section>

                            <div className="detail-view__compact-row">
                                <div className="detail-view__section detail-view__section--compact">
                                    {!loading && deviceData && <DeviceFMHistoryComponent data={deviceData}/>}
                                </div>

                                <DeviceLogsSection
                                    serialNumber={deviceData.device_serial_number}
                                    token={userData.token}
                                />
                            </div>

                            {mapExpanded && (
                                <section className="detail-view__section detail-view__map-section" ref={mapSectionRef}>
                                    {deviceData.device?.address && (
                                        <div className="detail-view__map-address">
                                            {deviceData.device?.address}
                                        </div>
                                    )}
                                    <YandexLocationMap
                                        lat={deviceData.device.config.lat}
                                        long={deviceData.device.config.long}
                                        className="detail-view__map"
                                    />
                                </section>
                            )}

                            <ExpandableSection
                                index="07"
                                icon={<ReceiptText size={16}/>}
                                title={t("sideBar.items.column8")}
                            >
                                <Orders serialNumber={deviceData.device_serial_number}/>
                            </ExpandableSection>

                            <div className="detail-view__section">
                                <div className="detail-view__related-search">
                                    <h2 className="detail-view__section-title">
                                        {t("pages.devices.detailColumns.relatedSection1.title")}
                                    </h2>

                                    <input
                                        type="text"
                                        className="detail-view__related-search--input"
                                        placeholder={t("pages.devices.detailColumns.relatedSection1.searchPlaceholder")}
                                        value={relatedSearch}
                                        onChange={e => setRelatedSearch(e.target.value)}
                                    />
                                </div>

                                <h3 className="detail-view__subtitle">
                                    {t("pages.devices.detailColumns.relatedSection1.subtitle")}: {relatedDevices.length}
                                </h3>

                                {renderRelatedDevices()}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DeviceDetail;
